// File routes need the login token. An <iframe>, <img> or new tab can't send
// an Authorization header, and a `?token=` URL leaks the token into history
// and server logs — so fetch with the header and hand out a blob: URL instead.
import { useEffect, useState } from 'react';

const OBJECT_URL_TTL_MS = 60000;

export async function fetchFileBlob(url) {
  const token = localStorage.getItem('desky_token') || '';
  const res = await fetch(url, { headers: { Authorization: `Bearer ${token}` } });
  if (!res.ok) throw new Error(`HTTP ${res.status}`);
  return res.blob();
}

/** `[objectUrl, failed]` for `url`; objectUrl is null while loading. */
export function useAuthedObjectUrl(url) {
  const [state, setState] = useState([null, false]);
  useEffect(() => {
    if (!url) return undefined;
    let cancelled = false;
    let made = null;
    fetchFileBlob(url)
      .then((blob) => {
        if (cancelled) return;
        made = URL.createObjectURL(blob);
        setState([made, false]);
      })
      .catch(() => { if (!cancelled) setState([null, true]); });
    return () => {
      cancelled = true;
      setState([null, false]);
      if (made) URL.revokeObjectURL(made);
    };
  }, [url]);
  return state;
}

/** Open the file in a new tab. The tab opens before the await so popup blockers allow it. */
export async function openFileInNewTab(url) {
  const tab = window.open('', '_blank');
  if (!tab) throw new Error('Popup blocked');
  try {
    const objectUrl = URL.createObjectURL(await fetchFileBlob(url));
    tab.location.href = objectUrl;
    setTimeout(() => URL.revokeObjectURL(objectUrl), OBJECT_URL_TTL_MS);
  } catch (e) {
    tab.close();
    throw e;
  }
}

export async function downloadFile(url, filename) {
  const objectUrl = URL.createObjectURL(await fetchFileBlob(url));
  const a = document.createElement('a');
  a.href = objectUrl;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(objectUrl);
}
